import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Lock, X, ShieldAlert, KeyRound } from 'lucide-react';
import API_BASE_URL from '../config/api';

const AdminPinModal = ({ isOpen, onClose, onSuccess, secretPin }) => {
    const [pin, setPin] = useState(['', '', '', '']);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const ref1 = useRef();
    const ref2 = useRef();
    const ref3 = useRef();
    const ref4 = useRef();
    const inputRefs = useMemo(() => [ref1, ref2, ref3, ref4], [ref1, ref2, ref3, ref4]);

    useEffect(() => {
        if (isOpen) {
            setPin(['', '', '', '']);
            setError('');
            setTimeout(() => {
                if (inputRefs[0].current) inputRefs[0].current.focus();
            }, 100);
        }
    }, [isOpen, inputRefs]);

    const handleChange = (index, value) => {
        if (!/^\d*$/.test(value)) return;

        const newPin = [...pin];
        newPin[index] = value.slice(-1);
        setPin(newPin);
        setError('');

        if (value && index < 3) {
            inputRefs[index + 1].current.focus();
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !pin[index] && index > 0) {
            inputRefs[index - 1].current.focus();
        }
    };

    const handleSubmit = useCallback(async (e) => {
        if (e) e.preventDefault();
        const pinString = pin.join('');
        if (pinString.length !== 4) return;

        setLoading(true);
        setError('');

        // If a secretPin is provided, verify locally (Master Key mode)
        if (secretPin) {
            setTimeout(() => {
                if (pinString === secretPin) {
                    onSuccess();
                    onClose();
                } else {
                    setError('Incorrect Master PIN');
                    setPin(['', '', '', '']);
                    inputRefs[0].current.focus();
                }
                setLoading(false);
            }, 600);
            return;
        }

        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/admin/verify_admin_pin.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ pin: pinString })
            });

            const data = await response.json();
            if (data.success) {
                onSuccess();
                onClose();
            } else {
                setError(data.message || 'Incorrect PIN');
                setPin(['', '', '', '']);
                inputRefs[0].current.focus();
            }
        } catch (err) {
            setError('System error. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [pin, onClose, onSuccess, inputRefs, secretPin]);

    useEffect(() => {
        if (isOpen && pin.every(digit => digit !== '')) {
            handleSubmit();
        }
    }, [pin, handleSubmit, isOpen]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[200] bg-white animate-in fade-in duration-500 flex flex-col overflow-hidden">
            {/* Split Screen Layout */}
            <div className="flex-1 flex flex-col lg:flex-row h-full">

                {/* Visual Side / Top (Mobile) */}
                <div className="lg:w-2/5 bg-gradient-to-br from-indigo-700 via-purple-700 to-indigo-900 flex flex-col items-center justify-center p-12 relative overflow-hidden">
                    {/* Abstract background shapes for premium look */}
                    <div className="absolute top-[-10%] left-[-10%] w-64 h-64 bg-white/5 rounded-full blur-3xl animate-pulse" />
                    <div className="absolute bottom-[10%] right-[-5%] w-80 h-80 bg-purple-500/10 rounded-full blur-3xl" />

                    <button
                        onClick={onClose}
                        className="absolute top-8 left-8 p-3 text-white/60 hover:text-white hover:bg-white/10 rounded-2xl transition-all flex items-center gap-2 font-bold text-sm backdrop-blur-sm border border-white/5"
                    >
                        <X size={20} />
                        <span className="hidden sm:inline">Cancel Access</span>
                    </button>

                    <div className="relative z-10 text-center flex flex-col items-center">
                        <div className="w-24 h-24 mb-8 bg-white/10 backdrop-blur-xl border border-white/20 rounded-[2rem] flex items-center justify-center shadow-2xl animate-in zoom-in-50 duration-700 delay-200">
                            <Lock className="text-white" size={48} strokeWidth={1.5} />
                        </div>
                        <h2 className="text-4xl lg:text-5xl font-black text-white tracking-tight mb-4 drop-shadow-sm">Panel Locked</h2>
                        <div className="h-1.5 w-16 bg-white/30 rounded-full mb-6" />
                        <p className="text-indigo-100 text-lg font-medium max-w-xs leading-relaxed opacity-80">
                            This area is protected by secondary authentication. Please verify your admin credentials.
                        </p>
                    </div>
                </div>

                {/* Input Side / Bottom (Mobile) */}
                <div className="flex-1 bg-white flex flex-col items-center justify-center p-8 lg:p-24 relative">
                    <div className="w-full max-w-md mx-auto">
                        <div className="mb-12 text-center lg:text-left">
                            <h3 className="text-2xl font-black text-gray-900 mb-2">Security Verification</h3>
                            <p className="text-gray-500 font-medium">Enter your 4-digit security PIN to continue</p>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-12">
                            <div className="flex justify-center lg:justify-start gap-4 sm:gap-6">
                                {pin.map((digit, idx) => (
                                    <input
                                        key={idx}
                                        ref={inputRefs[idx]}
                                        type="password"
                                        inputMode="numeric"
                                        maxLength={1}
                                        value={digit}
                                        onChange={(e) => handleChange(idx, e.target.value)}
                                        onKeyDown={(e) => handleKeyDown(idx, e)}
                                        className={`w-16 h-20 sm:w-20 sm:h-24 text-center text-3xl font-black rounded-3xl border-4 outline-none transition-all shadow-sm
                                            ${error
                                                ? 'border-red-100 bg-red-50 text-red-600 animate-shake'
                                                : 'border-gray-50 bg-gray-50/50 focus:border-indigo-600 focus:bg-white focus:shadow-xl focus:shadow-indigo-100 text-indigo-700'
                                            }
                                        `}
                                    />
                                ))}
                            </div>

                            {error && (
                                <div className="flex items-center gap-3 justify-center lg:justify-start text-red-500 text-sm font-black bg-red-50 py-4 px-6 rounded-2xl border border-red-100 animate-in slide-in-from-top-2">
                                    <ShieldAlert size={20} />
                                    {error}
                                </div>
                            )}

                            <div className="space-y-6">
                                <button
                                    type="submit"
                                    disabled={loading || pin.some(d => !d)}
                                    className={`w-full py-6 rounded-[2rem] font-black text-sm uppercase tracking-[0.2em] transition-all flex items-center justify-center gap-3
                                        ${loading || pin.some(d => !d)
                                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                            : 'bg-indigo-600 text-white shadow-2xl shadow-indigo-200 hover:bg-indigo-700 hover:-translate-y-1 active:scale-95'}
                                    `}
                                >
                                    {loading ? 'Authenticating...' : (
                                        <>
                                            <span>Authorize Session</span>
                                            <KeyRound size={20} />
                                        </>
                                    )}
                                </button>

                                <p className="text-center text-[10px] text-gray-400 font-bold uppercase tracking-[0.3em]">
                                    Secure Blockchain-ready Protocol v4.2
                                </p>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminPinModal;
